import axios from "axios";
import { store } from "@/store/store.js";
import { buildDashboardLink } from "@/helpers/urlHelper";

/**
 * Resolves store information from its alias. The dashboard for a store
 * always lives at https://{alias}.biyo.co, so this is computed locally
 * rather than looked up from a directory service.
 */
export default function getStoreInfo(storeAlias) {
  return Promise.resolve({
    ok: true,
    status: 200,
    json: () =>
      Promise.resolve({
        alias: storeAlias,
        dashboard_link: buildDashboardLink(storeAlias)
      })
  });
}

export async function createOrder(order) {
  const orderApiUrl = `${store.getters.baseUrl}/api/orderlive/addorder`;
  return new Promise((resolve, reject) => {
    axios
      .post(`${orderApiUrl}`, order)
      .then((res) => {
        console.log(res.status);
        resolve(res);
      })
      .catch((err) => {
        reject(err.response);
      });
  });
}

/**
 * Reconciles a checkout by its client idempotency key. Used on load after a
 * refresh/connection-loss while an order was pending: resolves to the order if
 * the previous attempt actually created one, or null if it didn't (404).
 * @param {string} idempotencyKey
 * @returns {Promise<Object|null>} The order data, or null if none exists yet.
 */
export async function fetchOrderByIdempotencyKey(idempotencyKey) {
  const url = `${store.getters.baseUrl}/api/orderlive/order/by-key/${idempotencyKey}/`;
  try {
    const res = await axios.get(url);
    return res.data;
  } catch (err) {
    if (err.response && err.response.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function login(loginCredentials) {
  const loginUrl = `${store.getters.baseUrl}/api/orderlive/login/`;
  // const loginUrl = 'http://localhost:8000/api/orderlive/login/'
  return new Promise((resolve, reject) => {
    axios
      .post(loginUrl, loginCredentials)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
}

export async function fetchAddresses(customerId) {
  const getAddressesUrl = `${store.getters.baseUrl}/api/orderlive/addresses/customer?id=${customerId}`;
  // const getAddressesUrl = `http://localhost:8000/api/orderlive/addresses/customer?id=${customerId}`
  return new Promise((resolve, reject) => {
    axios
      .get(getAddressesUrl)
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
}

export const fetchCountries = () => {
  const apiUrl = `${store.getters.baseUrl}/api/countries`;
  return new Promise((resolve, reject) => {
    axios
      .get(apiUrl)
      .then((res) => {
        if (res.status === 200) {
          resolve(res.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export const fetchUSStates = () => {
  const apiUrl = `${store.getters.baseUrl}/api/states`;
  return new Promise((resolve, reject) => {
    axios
      .get(apiUrl)
      .then((res) => {
        if (res.status === 200) {
          resolve(res.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};
export async function orderPickup(orderId, carModel, parkingNumber, token) {
  const url = `${store.getters.baseUrl}/api/orderlive/order-pickup/`;

  return new Promise((resolve, reject) => {
    axios
      .post(url, {
        order_id: orderId,
        car_model: carModel || "",
        parking_number: parkingNumber || "",
        token: token || ""
      })
      .then((res) => {
        console.log(res.status);
        resolve(res);
      })
      .catch((err) => {
        reject(err.response);
      });
  });
}
// ============================================
// Table Ordering API Functions (QR Code Self-Ordering)
// ============================================

/**
 * Decode MD5 hash to get table information
 * @param {string} tableHash - MD5 hash of table ID
 * @returns {Promise} - Table info including table_id, table_name, store_id, store_name
 */
export async function decodeTableHash(tableHash) {
  const url = `${store.getters.baseUrl}/api/orderlive/table/decode/${tableHash}/`;
  return axios.get(url);
}

/**
 * Get current open order for a table
 * @param {number} tableId - Table ID
 * @returns {Promise} - Order data or null if no open order
 */
export async function getTableOrder(tableId) {
  const url = `${store.getters.baseUrl}/api/orderlive/table/${tableId}/order/`;
  return axios.get(url);
}

/**
 * Create or add to table order
 * @param {object} orderData - Order data including table, related_store, items
 * @returns {Promise} - Created/updated order data
 */
export async function createTableOrder(orderData) {
  const baseUrl = store.getters.baseUrl;

  if (!baseUrl) {
    return Promise.reject({
      status: 500,
      data: { error: "Store base URL is not set" }
    });
  }

  // Ensure baseUrl doesn't have trailing slashes that would cause double slashes
  const cleanBaseUrl = baseUrl.replace(/\/+$/, "");
  const url = `${cleanBaseUrl}/api/orderlive/table/order/`;

  return new Promise((resolve, reject) => {
    axios
      .post(url, orderData)
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        console.error("[apiHelper] createTableOrder error:", err);
        console.error("[apiHelper] Request URL:", err.config?.url);
        reject(err.response || err);
      });
  });
}

/**
 * Add items to existing order
 * @param {number} orderId - Order ID
 * @param {array} items - Array of items to add
 * @returns {Promise} - Updated order data
 */
export async function addItemsToOrder(orderId, items) {
  const url = `${store.getters.baseUrl}/api/orderlive/order/${orderId}/add-items/`;
  return new Promise((resolve, reject) => {
    axios
      .post(url, { items })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err.response);
      });
  });
}

/**
 * Get modifier groups for a specific product
 * @param {number} productId - Product ID
 * @returns {Promise<Array>} - Array of modifier groups with nested modifiers
 */
export async function getProductModifierGroups(productId, storeId = null) {
  const baseUrl = store.getters.baseUrl;

  if (!baseUrl) {
    return Promise.reject({
      status: 500,
      data: { error: "Store base URL is not set" }
    });
  }

  const cleanBaseUrl = baseUrl.replace(/\/+$/, "");
  const params = storeId ? `?store_id=${encodeURIComponent(storeId)}` : "";
  const url = `${cleanBaseUrl}/api/orderlive/product/${productId}/modifier-groups/${params}`;

  return new Promise((resolve, reject) => {
    axios
      .get(url)
      .then((res) => {
        if (res.status === 200) {
          resolve(res.data);
        } else {
          reject({ status: res.status, data: res.data });
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          resolve([]);
        } else {
          reject(err.response || err);
        }
      });
  });
}
